<template>
  <main-input-dialog
    :is-visible.sync="computedIsVisible"
    :title-translation="$i18n.t('dialog.title.sendMessage')"
    agree-text="dialog.agreeSend"
    :inputs="inputs"
    :loading="isLoading"
    :reset-on-close="false"
    body-height="50vh"
    dialog-width="30%"
    @agree="messageSendHandler"
  >
    <template v-for="input of inputs" #[`${input.name}.input`]="data">
      <v-col :key="input.name" cols="6">
        <data-input v-model="payload[input.name]" v-bind="data" />
      </v-col>
    </template>
  </main-input-dialog>
</template>

<script>
import { get, call } from 'vuex-pathify';

import DataInput from '@/components/DataInput';

export default {
  name: 'SendMessageDialog',
  components: {
    DataInput,
    MainInputDialog: () => import('@/components/MainInputDialog'),
  },
  props: {
    isVisible: {
      type: Boolean,
      default: false,
    },
    selectedFeatures: {
      type: Array,
      default: () => [],
    },
    configurationList: {
      type: Array,
      default: () => [],
    },
    isSendEmailSmsConfigurationEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    payload: {},
    isLoading: false,
  }),
  computed: {
    currentLayer: get('layers/currentLayer'),
    currentLayerDataSource: get('layers/currentLayer@data_source_name'),
    computedIsVisible: {
      get() {
        return this.isVisible;
      },
      set(nV) {
        this.$emit('update:isVisible', nV);
      },
    },
    layerAttributes() {
      return this.$_flattenStructure(this.currentLayer.form_schema.elements).map(attr => {
        return { value: attr.attribute, text: attr.label };
      });
    },
    inputs() {
      const isEmail =
        !this.isSendEmailSmsConfigurationEnabled || this.configurationList.find(el => el.protocol === 'email');
      const isSms =
        !this.isSendEmailSmsConfigurationEnabled || this.configurationList.find(el => el.protocol === 'sms');
      const isEmailProtocol = this.payload.protocol === 'email';
      const inputs = [
        {
          name: 'protocol',
          translationPath: 'dialog.messageProtocol',
          dataType: { name: 'select' },
          items: [
            ...(isEmail ? [{ value: 'email', text: 'E-mail' }] : []),
            ...(isSms ? [{ value: 'sms', text: 'SMS' }] : []),
          ],
        },
        {
          name: 'attribute',
          translationPath: isEmailProtocol ? 'dialog.emailAttribute' : 'dialog.phoneAttribute',
          dataType: { name: 'select' },
          items: this.layerAttributes,
        },
        ...(isEmailProtocol
          ? [
              {
                name: 'subject',
                translationPath: 'dialog.emailSubject',
                dataType: { name: 'text' },
              },
            ]
          : []),
        {
          name: 'content',
          translationPath: isEmailProtocol ? 'dialog.messageEmailText' : 'dialog.messageSmsText',
          dataType: { name: 'textarea' },
          autoGrow: true,
          dense: true,
          rows: '4',
        },
      ];
      return inputs.map(input => {
        return {
          ...input,
          cols: 12,
          leftCol: 5,
          rightCol: 6,
          spacer: true,
          rules: [v => !!v || v == '0' || 'rules.required'],
        };
      });
    },
  },
  watch: {
    isVisible: {
      handler() {
        if (this.isSendEmailSmsConfigurationEnabled) {
          const configuration = JSON.parse(JSON.stringify(this.configurationList[0] || {}));
          delete configuration?.id;
          this.payload = configuration || {};
        } else {
          this.payload = {};
        }
      },
      immediate: true,
    },
    'payload.protocol'(nV) {
      if (this.isSendEmailSmsConfigurationEnabled) {
        const configuration = JSON.parse(JSON.stringify(this.configurationList || {})).find(el => el.protocol === nV);
        delete configuration?.id;
        this.payload = configuration || { protocol: nV };
      } else {
        this.payload = { protocol: nV };
      }
    },
  },
  methods: {
    sendMessage: call('sendMessage/sendMessage'),
    async messageSendHandler() {
      this.isLoading = true;
      const { protocol, ...params } = this.payload;
      if (protocol === 'sms') delete params.subject;
      const payload = {
        protocol,
        body: {
          ...params,
          ...(this.isSendEmailSmsConfigurationEnabled ? {} : { datasource: this.currentLayerDataSource }),
          ids: this.selectedFeatures,
        },
        bulk: this.isSendEmailSmsConfigurationEnabled,
      };
      try {
        await this.sendMessage(payload);
        this.$store.set('snackbar/PUSH_SUCCESSFULLY_MESSAGE!', {
          message: this.$i18n.t('success.messageWasSent'),
        });
      } catch (error) {
        console.log('  error', error);
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>
